import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCourses } from '../contexts/CourseContext';
import { useApplications } from '../contexts/ApplicationContext';
import { useAuth } from '../contexts/AuthContext';
import {
  CalendarIcon,
  MapPinIcon,
  UsersIcon,
  CurrencyDollarIcon,
  ClockIcon,
  UserIcon,
  CheckCircleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import Header from '../components/Header';

const ProgramDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const { getCourse } = useCourses();
  const { addApplication, getApplicationsByCourse, getApplicationsByStudent } = useApplications();
  const { user, isStudent } = useAuth();

  const course = getCourse(id || '');
  const courseApplications = getApplicationsByCourse(id || '');
  const currentApplications = courseApplications.filter(app =>
    app.status !== 'cancelled' && app.status !== 'rejected'
  );
  const userApplication = user ? getApplicationsByStudent(user.id).find(app =>
    app.courseId === id && app.status !== 'cancelled' && app.status !== 'rejected'
  ) : null;

  if (!course) {
    return (
      <div>
        <Header />
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
          <div className="text-center py-12">
            <p className="text-gray-500">강의를 찾을 수 없습니다.</p>
            <button
              onClick={() => navigate('/programs')}
              className="mt-4 inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              프로그램 목록으로 돌아가기
            </button>
          </div>
        </div>
      </div>
    );
  }

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('ko-KR');
  };

  const formatPrice = (price: number) => {
    return price.toLocaleString('ko-KR');
  };

  const isApplicationClosed = currentApplications.length >= course.maxStudents;
  const applicationDeadlinePassed = new Date(course.registrationEndDate) < new Date();

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'recruiting': return '모집중';
      case 'ongoing': return '진행중';
      case 'completed': return '종료';
      default: return '임시저장';
    }
  };

  const handleApplication = () => {
    if (!user || !isStudent) {
      alert('수강신청을 하려면 학생으로 로그인해주세요.');
      return;
    }

    if (userApplication) {
      alert('이미 신청한 강의입니다.');
      return;
    }

    if (isApplicationClosed) {
      alert('신청 정원이 마감되었습니다.');
      return;
    }

    if (applicationDeadlinePassed) {
      alert('신청 기간이 종료되었습니다.');
      return;
    }

    addApplication({
      courseId: course.id,
      studentId: user.id,
      status: 'pending',
      paymentStatus: 'pending',
      paymentAmount: course.tuition + (course.materialFee || 0)
    });

    alert('수강신청이 완료되었습니다.');
  };

  const getApplicationStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return '접수완료';
      case 'approved': return '결제대기';
      case 'rejected': return '신청취소';
      case 'waiting': return '대기중';
      case 'cancelled': return '신청취소';
      default: return status;
    }
  };

  return (
    <div>
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24">
      <button
        onClick={() => navigate('/programs')}
        className="mb-6 inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-1" />
        프로그램 목록으로 돌아가기
      </button>

      <div className="bg-white shadow-xl rounded-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-white bg-opacity-20 text-white">
                {getStatusLabel(course.status)}
              </span>
              <h1 className="mt-4 text-3xl font-bold text-white">{course.courseName}</h1>
              <p className="mt-2 text-blue-100">{course.courseDescription}</p>
            </div>
            {isApplicationClosed && (
              <span className="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold bg-red-500 text-white">
                모집 마감
              </span>
            )}
          </div>
        </div>

        <div className="px-6 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">프로그램 정보</h2>
              <dl className="space-y-4">
                <div className="flex items-start">
                  <CalendarIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <dt className="text-sm font-medium text-gray-500">신청 기간</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formatDate(course.registrationStartDate)} ~ {formatDate(course.registrationEndDate)}
                    </dd>
                  </div>
                </div>

                <div className="flex items-start">
                  <UsersIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <dt className="text-sm font-medium text-gray-500">신청 현황</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {currentApplications.length} / {course.maxStudents}명
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            isApplicationClosed ? 'bg-red-600' : 'bg-blue-600'
                          }`}
                          style={{ width: `${Math.min((currentApplications.length / course.maxStudents) * 100, 100)}%` }}
                        ></div>
                      </div>
                    </dd>
                  </div>
                </div>

                <div className="flex items-start">
                  <CalendarIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <dt className="text-sm font-medium text-gray-500">교육 기간</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formatDate(course.courseStartDate)} ~ {formatDate(course.courseEndDate)}
                    </dd>
                  </div>
                </div>

                <div className="flex items-start">
                  <ClockIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <dt className="text-sm font-medium text-gray-500">교육 시간</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {course.courseTime} ({course.courseDays.join(', ')})
                    </dd>
                  </div>
                </div>

                <div className="flex items-start">
                  <CurrencyDollarIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <dt className="text-sm font-medium text-gray-500">수강료</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formatPrice(course.tuition)}원</dd>
                  </div>
                </div>

                <div className="flex items-start">
                  <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <dt className="text-sm font-medium text-gray-500">교육 장소</dt>
                    <dd className="mt-1 text-sm text-gray-900">{course.location}</dd>
                  </div>
                </div>

                <div className="flex items-start">
                  <UserIcon className="h-5 w-5 text-gray-400 mt-0.5 mr-3" />
                  <div>
                    <dt className="text-sm font-medium text-gray-500">강사</dt>
                    <dd className="mt-1 text-sm text-gray-900">{course.instructor}</dd>
                  </div>
                </div>
              </dl>
            </div>

            <div>
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">강의 정보</h2>
                <dl className="space-y-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">총 세션 수</dt>
                    <dd className="mt-1 text-sm text-gray-900">{course.totalSessions}회</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">강의 주기</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {course.courseCycle === 'weekly' ? '주간' :
                       course.courseCycle === 'daily' ? '일일' :
                       course.courseCycle === 'monthly' ? '월간' : '집중'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">선발 방식</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {course.selectionMethod === 'first-come' ? '선착순' : '추첨'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">담당 기관</dt>
                    <dd className="mt-1 text-sm text-gray-900">{course.institution}</dd>
                  </div>
                  {course.contactPerson && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">담당자</dt>
                      <dd className="mt-1 text-sm text-gray-900">{course.contactPerson}</dd>
                    </div>
                  )}
                  {course.contactPhone && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">연락처</dt>
                      <dd className="mt-1 text-sm text-gray-900">{course.contactPhone}</dd>
                    </div>
                  )}
                </dl>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4 items-start">
              <button
                onClick={handleApplication}
                disabled={isApplicationClosed || applicationDeadlinePassed || !!userApplication || !isStudent}
                className={`w-full sm:w-auto px-8 py-3 rounded-md font-semibold text-white transition-colors ${
                  isApplicationClosed || applicationDeadlinePassed || !!userApplication || !isStudent
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {userApplication
                  ? `신청됨 (${getApplicationStatusLabel(userApplication.status)})`
                  : isApplicationClosed
                  ? '모집 마감'
                  : applicationDeadlinePassed
                  ? '신청 기간 종료'
                  : !isStudent
                  ? '학생 로그인 필요'
                  : '신청하기'}
              </button>

              {userApplication && (
                <div className="text-sm text-gray-600">
                  <div>신청일: {new Date(userApplication.applicationDate).toLocaleDateString('ko-KR')}</div>
                  <div>상태: {getApplicationStatusLabel(userApplication.status)}</div>
                  <div>금액: {userApplication.paymentAmount.toLocaleString()}원</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showApplicationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">알림</h3>
            <p className="text-gray-600 mb-6">
              신청하기 기능은 현재 준비 중입니다. 빠른 시일 내에 서비스를 제공하겠습니다.
            </p>
            <button
              onClick={() => setShowApplicationModal(false)}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              확인
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ProgramDetail;